import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Dev-only JSON-LD schema validator.
 *
 * Scans the document for all `<script type="application/ld+json">` blocks,
 * parses each, and warns in the dev console when:
 *   - a block contains invalid JSON
 *   - the same schema @type appears more than once on the same page
 *     (e.g. two FAQPage or two BreadcrumbList blocks — a common cause of
 *     duplicate structured data and Google Search Console errors)
 *
 * In production this component is a no-op (the validator body is guarded
 * by `import.meta.env.DEV`), so it adds zero runtime cost to the live
 * site.
 */
export function SchemaValidator() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const blocks = Array.from(
      document.querySelectorAll('script[type="application/ld+json"]'),
    );

    if (blocks.length === 0) return;

    const seen = new Map<string, number>();
    const duplicates: string[] = [];
    const invalid: string[] = [];

    blocks.forEach((el, idx) => {
      const raw = el.textContent?.trim() ?? "";
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw);

        // A block may be a single object or a @graph array.
        const types: string[] = [];
        if (Array.isArray(parsed)) {
          parsed.forEach((node) => {
            if (node && typeof node === "object" && node["@type"]) {
              types.push(String(node["@type"]));
            }
          });
        } else if (parsed && typeof parsed === "object") {
          if (parsed["@graph"] && Array.isArray(parsed["@graph"])) {
            parsed["@graph"].forEach((node: { "@type"?: unknown }) => {
              if (node && typeof node === "object" && node["@type"]) {
                types.push(String(node["@type"]));
              }
            });
          } else if (parsed["@type"]) {
            types.push(String(parsed["@type"]));
          }
        }

        types.forEach((t) => {
          const count = (seen.get(t) ?? 0) + 1;
          seen.set(t, count);
          if (count === 2) {
            duplicates.push(t);
          }
        });
      } catch {
        invalid.push(`block #${idx + 1}`);
      }
    });

    if (invalid.length > 0) {
      console.warn(
        `[SchemaValidator] Invalid JSON-LD on ${pathname}: ${invalid.join(", ")}`,
      );
    }
    if (duplicates.length > 0) {
      console.warn(
        `[SchemaValidator] Duplicate schema @type detected on ${pathname}: ${duplicates.join(", ")}. ` +
          "Each @type should appear at most once per page to avoid Google structured-data errors.",
      );
    }
  }, [pathname]);

  return null;
}
